import { useCallback, useEffect } from 'react'

import { useTranslation } from 'react-i18n-lite'

import { useSelector } from 'react-redux'

import {
  useNavigate,
  useParams,
  NavigateFunction,
  useSearchParams
} from 'react-router-dom'

import { sendMessageToParent } from '@yes.technology/react-toolkit'

import { AuthenticationType } from 'authentication/shared/types'

import Iframe from 'shared/components/Iframe'

import DuplicateInteractionError from './DuplicateInteractionError'

import useIframe from './hooks/useIframe'
import useToken from 'token/shared/hooks/useToken'
import { InteractionTaskNodeModel, NodeModel } from 'token/shared/types'
import ProcessExecutionState from 'workflow/shared/types/processExecutionState.type'
import useGateway from 'shared/hooks/useGateway'

type Params = {
  readonly workflowUuid: string
  readonly interactionUuid: string
}

const findNextAsyncNodemodel = (
  nodemodel: NodeModel | undefined,
  nodemodels: NodeModel[],
  sequences: ProcessExecutionState['flow_elementmodels']['sequences'],
  visited = new Set<string>()
): NodeModel | undefined => {
  if (!nodemodel || visited.has(nodemodel.uuid)) return undefined
  visited.add(nodemodel.uuid)
  const nextSequence = sequences.find(
    ({ source_nodemodel_uuid }) => source_nodemodel_uuid === nodemodel.uuid
  )
  const nextNodemodel = nodemodels.find(
    ({ uuid }) => uuid === nextSequence?.target_nodemodel_uuid
  )
  if (!nextNodemodel) return undefined
  if (nextNodemodel.type !== 'internalmethod_task_nodemodel')
    return nextNodemodel
  return findNextAsyncNodemodel(nextNodemodel, nodemodels, sequences, visited)
}

const Container: React.FunctionComponent = (): JSX.Element | null => {
  const { language } = useTranslation()

  const navigate: NavigateFunction = useNavigate()

  const { workflowUuid: tokenUuid = '', interactionUuid = '' } =
    useParams<Params>()

  const [searchParams] = useSearchParams()
  const hideInteractionContainer =
    searchParams.has('hideInteractionContainer') ?? false

  const urlInteraction = window.waInteractionUrl

  const authToken = useSelector(
    ({ authentication }: AuthenticationType) => authentication.token
  )

  const queryString = `token=${authToken}&lang=${language}&uuid_workflow=${tokenUuid}${
    hideInteractionContainer ? `&hideInteractionContainer=true` : ''
  }`

  const iframeSrc = `${urlInteraction}${interactionUuid}?${queryString}`

  const { token, metadata, refetch: refetchToken } = useToken({ tokenUuid })

  const { iframeAttributes, receiveMessage, isFinishedInteraction } = useIframe(
    {
      origin: urlInteraction,
      src: iframeSrc,
      title: 'Interaction',
      name: 'interaction'
    }
  )

  const executionState = token?.execution_state

  const nodes = executionState?.nodes || []
  const nodemodels = executionState?.flow_elementmodels?.nodemodels || []
  const sequences = executionState?.flow_elementmodels?.sequences || []

  const activeNode = nodes.find(
    ({ status }) => status !== window.doneStatusUUID
  )
  const activeNodemodel = nodemodels.find(
    ({ uuid }) => uuid === activeNode?.nodemodel_uuid
  )

  const isInteractionWaitingCompletion =
    activeNodemodel?.type === 'interaction_task_nodemodel' &&
    (activeNodemodel as InteractionTaskNodeModel).handler
      .interactionmodel_uuid === interactionUuid

  const nextAsyncNodemodel = findNextAsyncNodemodel(
    activeNodemodel,
    nodemodels,
    sequences
  )

  const isNextTaskAnInteraction =
    nextAsyncNodemodel?.type === 'interaction_task_nodemodel'

  const navigateToWorkflow = useCallback(() => {
    navigate(
      `/workflow/${tokenUuid}${hideInteractionContainer ? '?hideInteractionContainer=true' : ''}`,
      { replace: true }
    )
  }, [tokenUuid, hideInteractionContainer, navigate])

  const { navigateToForwardURL } = useGateway({ metadata })

  const updateNodemodels = useCallback(async () => {
    const { data } = await refetchToken()
    const updatedToken = data?.data?.token

    if (!updatedToken) return

    const updatedTasks = updatedToken.execution_state.nodes
    const hasInteractionWaitingToFill = updatedTasks.some(
      ({ status }) =>
        status?.toLocaleLowerCase() ===
        window.waitingInteractionFillUUID?.toLocaleLowerCase()
    )

    if (hasInteractionWaitingToFill) {
      navigateToWorkflow()
      return
    }

    navigateToForwardURL()
  }, [navigateToWorkflow, refetchToken, navigateToForwardURL])

  useEffect(() => {
    if (!token || !isFinishedInteraction) return

    const nodeModels = token.execution_state.flow_elementmodels.nodemodels
    const activeNodeModelIndex = nodeModels.findIndex(
      ({ uuid }) => uuid === activeNode?.nodemodel_uuid
    )
    const isLastTask = !nodeModels.slice(activeNodeModelIndex + 1).length

    if (isLastTask) {
      navigateToForwardURL()
      return
    }

    updateNodemodels()
  }, [
    token,
    isFinishedInteraction,
    updateNodemodels,
    activeNode,
    navigateToForwardURL
  ])

  if (isFinishedInteraction && token) {
    if (isNextTaskAnInteraction) {
      navigateToWorkflow()
    } else {
      sendMessageToParent(tokenUuid, interactionUuid)
    }
  }

  useEffect(() => {
    window.addEventListener('message', receiveMessage)

    return () => window.removeEventListener('message', receiveMessage)
  }, [receiveMessage])

  if (activeNodemodel && !isInteractionWaitingCompletion) {
    return <DuplicateInteractionError />
  }

  return (
    <Iframe {...iframeAttributes} allow='camera; geolocation; microphone' />
  )
}

export default Container
